import { Fragment, FunctionComponent } from 'react';
import Box from '@material-ui/core/Box';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Product, VendorCost } from '@spike/product-model';
import { Typography } from '@material-ui/core';
import VendorRow from './UI/VendorRow';
import { v4 as createUuid } from 'uuid';
import { clone } from 'lodash';
import ButtonV3 from 'components/UI/V3/Button/Button';

interface Props {
    product: Product;
    onChange: (product: Product) => void;
    onShowVendor: (vendorId: number) => void;
    onNext: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        vendorsContainer: {
            display: 'flex',
            flexDirection: 'column'
        },
        addVendor: {
            cursor: 'pointer',
            color: '#5E8677',
            fontWeight: 500,
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                marginTop: '13px',
                marginBottom: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                marginTop: '16px',
                marginBottom: '16px'
            }
        },
        actions: {
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            [theme.breakpoints.down(768)]: {
                'marginTop': '16px',
                'marginBottom': '8px',
                '& > button': {
                    width: '100%'
                }
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '24px',
                marginBottom: '39px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '32px',
                marginBottom: '48px'
            }
        }
    })
);

export const VendorsAndCost: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const createEmptyVendor = () => {
        return {
            default: false,
            cost: 0,
            uuid: createUuid()
        };
    };

    const handleAddVendor = () => {
        props.onChange({
            ...props.product,
            vendors: [...props.product.vendors, createEmptyVendor()]
        });
    };

    const handleRemoveVendor = (uuid: string) => {
        const updatedVendors = props.product.vendors.filter(vendor => vendor.uuid !== uuid);

        if (updatedVendors.length > 0 && !updatedVendors.some(vendor => vendor.default)) {
            updatedVendors[0].default = true;
        }

        props.onChange({ ...props.product, vendors: updatedVendors });
    };

    const handleChangeVendor = (changedVendor: VendorCost) => {
        props.onChange({
            ...props.product,
            vendors: props.product.vendors.map(vendor =>
                vendor.uuid === changedVendor.uuid ? clone(changedVendor) : vendor
            )
        });
    };

    const handleChangeDefaultVendor = (uuid: string) => {
        props.onChange({
            ...props.product,
            vendors: props.product.vendors.map(vendor => ({
                ...vendor,
                default: vendor.uuid === uuid
            }))
        });
    };

    return (
        <Fragment>
            <Box className={classes.container}>
                <Box className={classes.vendorsContainer}>
                    {props.product.vendors.map(vc => {
                        return (
                            <VendorRow
                                key={vc.uuid}
                                product={props.product}
                                vendorCost={vc}
                                onChange={handleChangeVendor}
                                onRemove={handleRemoveVendor}
                                onShowVendor={props.onShowVendor}
                                onSetAsDefault={handleChangeDefaultVendor}
                            />
                        );
                    })}
                    <Typography
                        className={classes.addVendor}
                        onClick={handleAddVendor}
                    >
                        + Add another supplier
                    </Typography>
                </Box>
                <Box className={classes.actions}>
                    <ButtonV3
                        label="Next"
                        onClick={props.onNext}
                        size="large"
                        variant="primary"
                    />
                </Box>
            </Box>
        </Fragment>
    );
};

export default VendorsAndCost;
